import styled, { css, x } from '@xstyled/styled-components';
import React, { useEffect, useMemo, useState } from 'react';
import { Item, registry, RenderProps } from '../Item';
import { MESSAGE_RESPONSE } from '../itemIds';
import { useMessageStore } from './MessageStore';
import audioEffectRightAnswer from '~/assets/mp3/right_answer.mp3';
import audioEffectWrongAnswer from '~/assets/mp3/wrong_answer.mp3';
import newMessageImgIcon from './chooseAnswer.png';
import tail from './tail.svg';
import { t } from 'ttag';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { select } from '~/lib/select';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { TrainingState, useTrainingStore } from '~/training/state';
import { useEditorStore, SequenceItem } from '~/shell/Store';
import { shouldSendInteractions, useLmsAdapter } from '~/lms';
import LightButton from '~/editor/buttons/LightButton';
import { TrashIconForBtn } from '~/training/sequence/templates/items';
import { Switcher } from '~/editor/buttons/Switcher';
import { AudioPlayerButton } from '../button/BigPlayButton';

interface Props {
  id?: string;
  group: string;
  value: [];
  type: string;
  correct: boolean;
  feedback?: string;
  feedbackAudios?: {
    id?: string | number;
    mediaUrl: string;
    subtitlesUrl: string;
    voiceId?: string;
  };
  llamaSurvey?: boolean | undefined | null;
  correctAnswers?: [] | undefined | null;
  disableBlockers?: boolean;
}

interface TextMessageProps {
  status?: string;
}

const Tail = styled(tail)`
  width: 22px;
  height: 23px;
  position: absolute;
  right: -5px;
  margin-top: -7px;
  fill: ${p => p.theme.colors.blueMed};
  transform: scale(-1, 0.7);
`;

const WrapperForTextMessage = styled.divBox`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 20px;

  .textMessageItem {
    margin-top: -4px;
    margin-right: 10px;
    margin-left: 0px !important;
    width: 160px;
  }
`;

const TextMessage = styled.labelBox<TextMessageProps>`
  display: table;
  background-color: ${p => p.theme.colors.blueMed};
  border-radius: 20px;
  margin-top: 15px;
  margin-left: 45px;
  padding: 10px;
  padding-left: 15px;
  padding-right: 15px;
  color: white;
  position: relative;
  cursor: pointer;
  text-align: left;
  --status-drop-shadow: 0 0 #0000;
  --focus-ring-shadow: 0 0 #0000;
  box-shadow: var(--status-drop-shadow), var(--focus-ring-shadow);

  &:focus {
    outline: 2px solid blue;
    outline-offset: 6px;
    --focus-ring-shadow: 0 0 0 2px white;
  }
  ${p =>
    p.status === 'llamaSurvey'
      ? css`
          color: black;
          background-color: white;
          border-left: 2px solid var(--color-grey-dk);
          border-right: 4px solid var(--color-grey-dk);
          border-top: 2px solid var(--color-grey-dk);
          --status-drop-shadow: 0px 4px 0px var(--color-grey-dk);
          > svg {
            fill: var(--color-grey-dk);
            right: -8px;
            margin-top: -4px;
            position: absolute;
          }
        `
      : ''}
  ${p =>
    p.status === 'correct'
      ? css`
          color: black;
          background-color: white;
          border-left: 2px solid ${p => p.theme.colors.greenDark};
          border-right: 4px solid ${p => p.theme.colors.greenDark};
          border-top: 2px solid ${p => p.theme.colors.greenDark};
          --status-drop-shadow: 0px 4px 0px ${p => p.theme.colors.greenDark};
          &:after {
            content: '✔';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            border-radius: 14px;
            background-color: greenDark;
            top: -14px;
            right: -14px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          > svg {
            fill: ${p => p.theme.colors.greenDark};
            right: -8px;
            margin-top: -4px;
            position: absolute;
          }
        `
      : ''}
  ${p =>
    p.status === 'wrong'
      ? css`
          color: black;
          background-color: white;
          border-left: 2px solid ${p => p.theme.colors.redDark};
          border-right: 4px solid ${p => p.theme.colors.redDark};
          border-top: 2px solid ${p => p.theme.colors.redDark};
          --status-drop-shadow: 0px 4px 0px ${p => p.theme.colors.redDark};
          &:after {
            content: '!';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            border-radius: 14px;
            background-color: redDark;
            top: -14px;
            right: -14px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          > svg {
            fill: ${p => p.theme.colors.redDark};
            right: -8px;
            margin-top: -4px;
            position: absolute;
          }
        `
      : ''}
`;

const Circle = styled.divBox<TextMessageProps>`
  background-color: ${p => p.theme.colors.greyMed};
  border-radius: 50%;
  width: 26px;
  height: 26px;
  float: left;
  margin-top: 10px;
  cursor: pointer;
  position: relative;
  ${p =>
    p.status === 'llamaSurvey'
      ? css`
          border: 3px solid var(--color-grey-dk);
        `
      : ''}
  ${p =>
    p.status === 'correct'
      ? css`
          border: 3px solid ${p => p.theme.colors.greenDark};
        `
      : ''}
  ${p =>
    p.status === 'wrong'
      ? css`
          border: 3px solid ${p => p.theme.colors.redDark};
        `
      : ''}
`;

const Description = styled.divBox`
  font-size: 12px;
  line-height: 15px;
  text-align: right;
  color: ${p => p.theme.colors.greyDark};
`;

const WrapperForAudioPlayer = styled.div<{ notActiveAudioPlayer?: boolean }>`
  .audioPlayerButtonDiv {
    width: 50px !important;
    height: 50px !important;
    margin-left: 10px;

    .drillBoxAnimation {
      width: 45px !important;
      height: 45px !important;
      border-radius: 10px;

      display: none !important;
    }

    .playButton {
      border-radius: 10px !important;
      min-width: 40px !important;
      width: 40px !important;
      height: 40px !important;
    }
  }

  ${p =>
    p.notActiveAudioPlayer &&
    css`
      .audioPlayerButtonDiv {
        .playButton {
          background-color: #f8f1ff !important;

          svg {
            fill: #9738ff !important;
          }
        }

        .playButton:hover {
          background-color: #9738ff !important;

          svg {
            fill: #fff !important;
          }
        }
      }
    `}
`;

class soundEffect {
  audioRightAnswer: HTMLAudioElement;
  audioWrongAnswer: HTMLAudioElement;

  constructor() {
    this.audioRightAnswer = new Audio(audioEffectRightAnswer);
    this.audioRightAnswer.load();

    this.audioWrongAnswer = new Audio(audioEffectWrongAnswer);
    this.audioWrongAnswer.load();
  }

  play(answer: boolean) {
    if (answer) {
      this.audioRightAnswer.play().catch(() => {
        // Silently ignore autoplay restrictions
      });
    } else {
      this.audioWrongAnswer.play().catch(() => {
        // Silently ignore autoplay restrictions
      });
    }
  }
}

const TitleChooseCorrectAnswer = () => {
  return (
    <x.div>
      <x.img
        className="invertStyleForDarkMode"
        src={newMessageImgIcon}
        marginLeft="auto"
        marginTop="20px"
        aria-hidden={true}
        alt="..."
      />
      <Description>{t`Choose the correct answer(s)`}</Description>
    </x.div>
  );
};

const mediaSelector = (s: MediaPlayerStore) => select(s, 'reset', 'loadMedia');
const selector = (s: TrainingState) =>
  select(s, 'saveQuestionAnswers', 'auditoryFeedback', 'status');

export class MessageResponse extends Item<typeof MESSAGE_RESPONSE, Props> {
  protected render({
    id,
    correct,
    children,
    feedback,
    feedbackAudios,
    group,
    llamaSurvey,
    correctAnswers,
    disableBlockers
  }: RenderProps<Props>) {
    const { loadMedia, reset } = useMediaPlayerStore(mediaSelector);
    const { saveQuestionAnswers, auditoryFeedback, status } =
      useTrainingStore(selector);
    const { addBlocker, removeBlocker } = useSequenceStore();
    const lms = useLmsAdapter();
    const [selectedAnswer, setSelectedAnswer] = useState('default');
    const {
      editorMode,
      contentEditable,
      isReadOnlyView,
      sequence,
      removeSubItem,
      updateSequence,
      updateSequenceInStore
    } = useEditorStore();
    const [isCorrect, setIsCorrect] = useState<boolean>(correct);
    const [showFormForFeedback, setShowFormForFeedback] =
      useState<boolean>(!!feedback);
    const [localFeedback, setLocalFeedback] = useState(feedback || '');

    const {
      activeItems,
      setStatus,
      firstElement,
      setFirstElement,
      setFeedback,
      narration,
      subtitlesUrl,
      playAudio
    } = useMessageStore();

    const sound = useMemo(() => new soundEffect(), []);

    useEffect(() => {
      setFeedback('');

      if (!disableBlockers && correct) {
        addBlocker(group);
      }
    }, []);

    useEffect(() => {
      if (narration) {
        reset();

        if (!llamaSurvey) {
          setTimeout(() => {
            loadMedia(narration, subtitlesUrl, 'audio', playAudio);
          }, 0);
        }
      }
    }, [narration, playAudio]);

    const handleDelete = () => {
      removeSubItem(id);
    };

    const clickOnItem = () => {
      if (editorMode) {
        return false;
      }

      if (status !== 'completed') {
        //Save question answers
        saveQuestionAnswers(id);
      }

      if (!disableBlockers) {
        removeBlocker(group);
      }
      setStatus(id, correct);

      if (!llamaSurvey) {
        setFeedback(feedback || '');
      }

      if (!llamaSurvey && auditoryFeedback) {
        sound.play(correct);
      }

      setSelectedAnswer(selectedAnswer == 'default' ? 'selected' : 'default');

      reset();

      if (!llamaSurvey && feedbackAudios && feedbackAudios.mediaUrl) {
        loadMedia(
          feedbackAudios.mediaUrl + '?t=' + id,
          feedbackAudios?.subtitlesUrl + '?t=' + id,
          'audio',
          true,
          0
        );
      }

      const [_group, _sequenceId, _questionId] = group.split('_');

      if (correctAnswers && shouldSendInteractions() && lms.isAvailable) {
        if (correctAnswers.length > 1) {
          const userSelectedAnswers = activeItems.map(i => i.id);

          const arraysMatch = (arr1: string[], arr2: string[]): boolean => {
            return (
              arr2.every((item: string) => arr1.includes(item)) &&
              arr1.every((item: string) => arr2.includes(item))
            );
          };

          lms.recordMultipleChoiceInteraction(
            _questionId || group,
            userSelectedAnswers || [],
            arraysMatch(correctAnswers, userSelectedAnswers),
            correctAnswers
          );
        } else {
          lms.recordTrueFalseInteraction(
            _questionId || group,
            correct,
            correct,
            true
          );
        }
      }
    };

    if (!firstElement) {
      setFirstElement(id);
    }

    const getCountOfItems = () =>
      sequence?.items?.filter(
        (item: SequenceItem) => item.type == 'QUESTION'
      )[0]?.items?.length;

    const selected = activeItems.find(el => el.id == id);

    const SetAnswerForButton = (value: string) => {
      setIsCorrect(value == 'true' ? true : false);
      updateSequence(id, 'correct', value == 'true' ? true : false);
    };

    const AddExplanation = () => {
      setShowFormForFeedback(true);
      const defaultFeedback = isCorrect ? 'It is correct' : 'It is incorrect';
      setLocalFeedback(defaultFeedback);
      updateSequence(id, 'feedback', defaultFeedback);
    };

    const RemoveExplanation = () => {
      setShowFormForFeedback(false);
      updateSequence(id, 'feedback', '');
      updateSequence(id, 'audio_ids', []);
      updateSequenceInStore(id, 'feedbackAudios', null);
    };

    const handleFeedbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalFeedback(e.target.value);
    };

    const handleFeedbackBlur = () => {
      updateSequence(id, 'feedback', localFeedback);
    };

    const getStatus = () => {
      if (isReadOnlyView) {
        return correct ? 'correct' : 'wrong';
      }
      if (selectedAnswer != 'default' && llamaSurvey) {
        return 'llamaSurvey';
      }
      return selected ? selected.status : '';
    };

    return (
      <>
        {firstElement == id && !editorMode && <TitleChooseCorrectAnswer />}
        <x.div>
          {!editorMode && (
            <Circle
              onClick={clickOnItem}
              status={getStatus()}
              role={'checkbox'}
              aria-checked={Boolean(selected?.status)}
            />
          )}
          {!editorMode && (
            <TextMessage
              role="button"
              as="button"
              onClick={clickOnItem}
              status={getStatus()}
            >
              {children}
              <Tail aria-hidden={true} />
            </TextMessage>
          )}
          {isReadOnlyView && (
            <Circle
              status={getStatus()}
              role={'checkbox'}
              aria-checked={correct}
            />
          )}
          {isReadOnlyView && (
            <TextMessage as="div" status={getStatus()}>
              {children}
              <Tail aria-hidden={true} />
            </TextMessage>
          )}
          {contentEditable && (
            <WrapperForTextMessage>
              <div style={{ display: 'flex', gap: '8px' }}>
                <TextMessage className="textMessageItem" as="div">
                  {children}
                  <Tail aria-hidden={true} />
                </TextMessage>
                <Switcher
                  value={isCorrect.toString()}
                  onChange={SetAnswerForButton}
                />
                {getCountOfItems()! > 1 && (
                  <LightButton
                    title="Remove"
                    onClick={handleDelete}
                    style={{
                      padding: '11px',
                      width: '38px',
                      height: '38px',
                      display: 'block',
                      marginLeft: '5px',
                      zIndex: 2
                    }}
                  >
                    <TrashIconForBtn
                      style={{ marginLeft: '-5px', marginTop: '-5px' }}
                    />
                  </LightButton>
                )}
              </div>
              {showFormForFeedback ? (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '10px'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      value={localFeedback}
                      onChange={handleFeedbackChange}
                      onBlur={handleFeedbackBlur}
                      style={{
                        width: '100%',
                        border: '1px solid #000',
                        backgroundColor: '#FFFDE8',
                        borderRadius: '8px',
                        padding: '8px 14px'
                      }}
                    />
                  </div>
                  <WrapperForAudioPlayer notActiveAudioPlayer={!feedbackAudios}>
                    <AudioPlayerButton
                      id={`${id}_audios`}
                      mediaUrl={feedbackAudios ? feedbackAudios.mediaUrl : ''}
                      audioId={feedbackAudios ? feedbackAudios.id : ''}
                      clearSubitemAudio={true}
                    />
                  </WrapperForAudioPlayer>
                  <div
                    style={{ marginLeft: '8px', marginRight: '3px' }}
                    tabIndex={-1}
                  >
                    <LightButton
                      onClick={RemoveExplanation}
                      style={{
                        padding: '11px',
                        width: '38px',
                        height: '38px'
                      }}
                    >
                      <TrashIconForBtn
                        style={{ marginLeft: '-5px', marginTop: '-5px' }}
                      />
                    </LightButton>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '10px'
                  }}
                >
                  <LightButton
                    onClick={AddExplanation}
                    style={{ padding: '11px' }}
                  >
                    Add answer feedback
                  </LightButton>
                </div>
              )}
            </WrapperForTextMessage>
          )}
        </x.div>
      </>
    );
  }
}

registry[MESSAGE_RESPONSE] = MessageResponse;
